import { ChangeDetectionStrategy, Component, EventEmitter, Inject, Input, OnInit, Optional, Output, signal, ViewChild } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SearchFilter, SearchSelect, SearchSort, TableService } from '@fry/lib/list';
import { EasTable, EasTableColumn, EasTableMeta, EasTableRow, EXPAND_STATE } from '@fry/lib/store';
import { BehaviorSubject, catchError, combineLatest, delay, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { SelectedItems } from './interactive-table.interface';
import { EasBuilderField, EasBuilderGroup } from '../../form-builder/builder.interfaces';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { FilterTableService } from './filters-selector/filters-table.service';
import { COLUMN_MODAL, COLUMN_MODAL_COMPONENT } from '../../table/table.component';
import { APIService } from '@fry/lib/api';
import { MatDialog } from '@angular/material/dialog';
import { NotifyService, objectIdentifier } from '@fry/lib/utils';
import { EasFieldType, EasFormGroup, EasFormService } from '../../easform';
import { BuilderFormService } from '../../form-builder/form.service';
import { TableExportsService } from './table-exports/table-exports.service';
import { isArray, isEqual } from 'lodash';
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { DIALOG_SIZES, DialogConfig } from '@fry/lib/core/dialog-config.service';

export interface InteractiveTableOptionsAll {
  enableFilters: boolean;
  enableExport: boolean;
  enableColumns: boolean;
  enableSelection: boolean;
  enablePagination: boolean;
  enableControls: boolean;
  enableExpand: boolean;
  itemSingleLabel: string;
  itemPluralLabel: string;
  tableTitle?: string;
  layout?: 'table' | 'auto'; // 'rows' doesn't work yet
  expand?: {
    label?: string;
    type?: "link" | "modal";
    modalType?: string;
  };
  context: {
    user?: string;
  }
}

export type InteractiveTableOptions = Partial<InteractiveTableOptionsAll>;


const defaultOptions: InteractiveTableOptionsAll = {
  enableControls: true,
  enableFilters: true,
  enableExport: false,
  enableColumns: true,
  enableSelection: false,
  enableExpand: false,
  enablePagination: true,
  itemSingleLabel: $localize `result`,
  itemPluralLabel: $localize `results`,
  context: {},
  layout: 'auto'
}

/**
 * @description This component is a wrapper around the table component using a mat-table
 * It adds the following features:
 * - Columns selector
 * - Column reordering
 * - Export
 * - Pagination
 * - Sorting
 * - Search and Filtering
 * - Row selection in two ways: manually or by search
 *
 * The data is loaded from the TableSouce from the TableService.
 * It communicates with the backend using the APIService and the SearchModel via {doc_type}/table
 * more info: https://github.com/Fry-IT/eas/wiki/Interactive-Table
 */

@Component({
    selector: 'eas-interactive-table',
    templateUrl: './interactive-table.component.html',
    styleUrls: ['./interactive-table.component.css', './table-controls.common.css'],
    providers: [
        FilterTableService,
        TableExportsService
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class InteractiveTableComponent implements OnInit {

  @Input() options!: Partial<InteractiveTableOptions>;
  @Input() initialSelectedItems!: SelectedItems;
  @Output() selectedItems = new EventEmitter<SelectedItems>();

  $tableRows = signal<EasTableRow[]>([]);

  public tableOptions: InteractiveTableOptionsAll;
  easForm: EasFormGroup | null = null;
  quickEasForm: EasFormGroup | null = null;

  private $destroyed = new Subject<void>();

  // IDs required for ARIA attributes
  _id = objectIdentifier('rsr-');
  columnsPanelId = `columns-panel-${this._id}`;
  columnsButtonId = `columns-button-${this._id}`;
  filtersPanelId = `filters-panel-${this._id}`;
  filtersButtonId = `filters-button-${this._id}`;
  exportPanelId = `export-panel-${this._id}`;
  exportButtonId = `export-button-${this._id}`;

  tableReady = false;

  EXPAND_STATE = EXPAND_STATE;

  // table
  tableRows: EasTableRow[] = [];
  // state: string = 'loading';
  $state = toSignal(this.list.state);

  // filters
  searchFilter: SearchFilter | undefined;
  showFiltersSelector = false;
  extraFilters = {}; // filters that are set on top of already existing filters by the module
  featuredFilterDef: any[] = [];
  quickFilterDef: any[] = [];
  haveFilters = false;

  // columnsSelector: ColumnsSelectorComponent;
  private stickedColunns: string[] = [];
  showColumnsSelector = false;
  allAvailableColumns: EasTableColumn[] = [];
  selectedColumnsDef: EasTableColumn[] = [];
  private _selectedColumnsIds: string[] = [];
  defaultColumns: SearchSelect[] = [];

  // exports
  showTableExports = false;

  // pagination
  $paginationMeta = signal<EasTableMeta>({selectedColumns: [], size: 0, start: 0, total: 0});

  // sorting
  @ViewChild(MatSort) sort!: MatSort;

  // rows selection
  isAllRowsInPageChecked = false
  isManuallySelectingRows = true; // if true, the user is selecting rows manually, if false, the user is selecting all rows matching the search
  selectedRowIds: string[] = [];
  selectedRowsCount = 0;


  filterFormDef: EasBuilderField[] | null = null;

  filtersCount$ = this.filterService.filterCount$;
  private featuredReadable = new BehaviorSubject<SearchFilter>({});
  private quickReadable = new BehaviorSubject<SearchFilter>({});
  private pageLoaded = new BehaviorSubject<void>(undefined);

  public itemLabels: any = {single: $localize `item`, plural: $localize `items`}
  public searchText$ = combineLatest([
    this.filtersCount$,
    this.featuredReadable,
    this.quickReadable,
    this.pageLoaded,
  ]).pipe(
    map(([filterCount, readable, quickReadable]) => {
      readable = {...readable, ...quickReadable};
      const itemsLabel = this.$paginationMeta().total > 1 ? this.tableOptions.itemPluralLabel : this.tableOptions.itemSingleLabel;
      let preText = $localize `Found <span class="text-accent-600">${this.$paginationMeta().total}</span> ${itemsLabel}`;

      const statements: string[] = [];
      Object.entries(readable).forEach((([key, value]) => {
        if (value === '' || value === null || value === undefined || ((isArray(value) && value.length === 0))) {
          return;
        }

        if (isArray(value)) {
          value = value.join(", ");
        }
        statements.push(`${key.toLocaleLowerCase()} <span class="text-accent-600">${value}</span>`)
      }))

      if (filterCount === 1) {
        statements.push(`${filterCount} filter`);
      } else if (filterCount > 1) {
        statements.push(`${filterCount} filters`);
      }

      if (statements.length) {
        const joinedStatements = statements.join($localize ` and `);
        preText = $localize `${preText} matching ${joinedStatements}`;
      }

      return this.sanitizer.bypassSecurityTrustHtml(preText);
    })
  );

  constructor(
    public list: TableService,
    private filterService: FilterTableService,
    private easform: EasFormService,
    private formService: BuilderFormService,
    private api: APIService,
    private matDialog: MatDialog,
    private notify: NotifyService,
    private sanitizer: DomSanitizer,
    private dialogConfig: DialogConfig,
    @Optional() @Inject(COLUMN_MODAL) private columnModals: COLUMN_MODAL_COMPONENT[],
  ) {
    this.tableOptions = { ...defaultOptions };
  }

  ngOnInit(): void {
    this.itemLabels = {
      single: this.tableOptions.itemSingleLabel,
      plural: this.tableOptions.itemPluralLabel
    };

    this.loadSelected();
  }

  ngOnChanges(): void {
    this.tableOptions = {
      ...defaultOptions,
      ...this.options
    }

    if (this.tableOptions.enableSelection) {
      if (this.stickedColunns.indexOf('select') === -1) {
        this.stickedColunns.unshift('select')
        if (this.tableReady) {
          this.constructSelectedColumns(this._selectedColumnsIds)
        }
      }
    }

    if (this.tableOptions.enableExpand) {
      if (this.stickedColunns.indexOf('expand') === -1) {
        this.stickedColunns.unshift('expand')
        if (this.tableReady) {
          this.constructSelectedColumns(this._selectedColumnsIds)
        }
      }
    }
  }

  ngAfterViewInit(): void {
    this.setupTableRowsListener();
  }

  // Table
  private setupTableRowsListener() {
    this.handleDefinition();
  };

  private handleDefinition() {
    // This is super ugly but we need to ensure
    // it happens in the next changes detection after
    // it is constructed
    this.list.EasTableDefinition.pipe(
      delay(0)
    ).subscribe(tableDefinition => {
        this.defaultColumns = this.list.getDefaultSelect();

        const filterFormDef = this.list.getFiltersFromDef(tableDefinition.filters?.filterFormDef);
        this.featuredFilterDef = filterFormDef.featured;
        this.quickFilterDef = filterFormDef.quick;
        this.haveFilters = filterFormDef.normal.length > 0;
        this.allAvailableColumns = tableDefinition.columns;

        // set saved sort
        let fixedModel = this.list.getFixedModel();
        let searchModel = this.list.getSearchModel();
        if (searchModel.sort.sort_on) {
          this.sort.active = searchModel.sort.sort_on;
        } else if (fixedModel.sort.sort_on) {
          this.sort.active = fixedModel.sort.sort_on;
        }

        if (searchModel.sort.sort_order) {
          this.sort.direction = searchModel.sort.sort_order;
        } else if (fixedModel.sort.sort_order) {
          this.sort.direction = fixedModel.sort.sort_order;
        }

        this.filterService.initialize(filterFormDef, searchModel.filter);
        this.setupSortEventListener();
        this.setupFilterListener();
        this.setupFeaturedFilter();
        this.setupQuickFilter();
        this.handleTableData();
        this.tableReady = true;
    })
  }

  private handleTableData() {
    this.list.table.pipe(
      takeUntil(this.$destroyed)
    ).subscribe((easTable: EasTable) => {
      // table
      this.tableRows = easTable.data;
      this.$tableRows.set(this.tableRows);

      if (easTable.meta) {
        this.$paginationMeta.set(easTable.meta);
        if (this._selectedColumnsIds.length === 0) {
          let columns;
          if (easTable.meta.selectedColumns !== undefined) {
            columns = easTable.meta.selectedColumns;
          } else {
            columns = this.allAvailableColumns.map(itm => itm.id);
          }
          this.constructSelectedColumns(columns);
        }
        this.refreshSelectedRowsCount();
        this.emitSelected();
      }

      this.checkAllRowsInPageChecked();
      this.pageLoaded.next();
    });
  }

  getButtonAriaLabel(label: string, count: number): string {
    return $localize `${label}, ${count} selected`;
  }

  onRefresh() {
    this.list.load().subscribe();
  }

  // Filters
  setupFilterListener() {
    this.filterService.searchFilter$.pipe(
      switchMap(filter => {
        if (filter === null) {
          return of(true);
        }
        // this.onComponentVisibilityChange('filtersSelector', 'hide');
        this.list.setPage(1);
        this.list.setSearchFilter(filter);
        return this.list.load().pipe(
          catchError(err => {
            console.log(err);
            return of(true);
          })
        );
      }),
      takeUntil(this.$destroyed)
    ).subscribe();
  }

  get selectedColumns() {
    const allColumns = this.allAvailableColumns.map(item => item.id);
    const extraColumns = this._selectedColumnsIds.filter(item => allColumns.indexOf(item) !== -1);
    if (this.tableOptions.enableSelection || this.tableOptions.enableExpand) {
      return [
        ...this.stickedColunns,
        ...extraColumns
      ]
    } else {
      return extraColumns;
    }
  }

  setupFeaturedFilter() {
    const buildForm: EasBuilderGroup = {
      id: 'root',
      fields: this.featuredFilterDef,
      type: EasFieldType.Group,
      fieldType: 'why?'
    }

    this.formService.buildForm(buildForm, { user: '__bulk__' }).pipe(
      map(group => {
        this.easForm = this.easform.createForm(group, this.filterService.featuredInitialFilter);
        this.easForm.getDebouncedChanges({ debounceTime: 1000}).pipe(
          takeUntil(this.$destroyed)
        )
        .subscribe(value => {
          console.log(value);
          this.filterService.applyFeaturedFilters(value);
        });
        this.easForm.getReadableChanges({ debounceTime: 1000 }).pipe(
          takeUntil(this.$destroyed)
        ).subscribe(value => {
          console.log('Readable', value);
          this.featuredReadable.next(value);
        });
      })
    ).subscribe();
  }

  setupQuickFilter() {
    const buildForm: EasBuilderGroup = {
      id: 'root',
      fields: this.quickFilterDef,
      type: EasFieldType.Group,
      fieldType: 'why?'
    }

    this.formService.buildForm(buildForm, { user: '__bulk__' }).pipe(
      map(group => {
        this.quickEasForm = this.easform.createForm(group, this.filterService.quickInitialFilter);
        this.quickEasForm.getDebouncedChanges({ debounceTime: 1000}).pipe(
          takeUntil(this.$destroyed)
        )
        .subscribe(value => {
          console.log(value);
          this.filterService.applyQuickFilters(value);
        });
        this.quickEasForm.getReadableChanges({ debounceTime: 1000 }).pipe(
          takeUntil(this.$destroyed)
        ).subscribe(value => {
          console.log('Readable', value);
          this.quickReadable.next(value);
        });
      })
    ).subscribe();
  }

  // Columns selector
  onColumnSelected(selectedIds: string[]) {
    this.constructSelectedColumns(selectedIds);
    this.list.setSearchSelect(selectedIds);
    this.list.load().subscribe();
    // this.onComponentVisibilityChange('columnsSelector');
  }

  constructSelectedColumns(selectedColumns: string[]) {
    this._selectedColumnsIds = [
      ...selectedColumns
    ]
    this.selectedColumnsDef = this.allAvailableColumns.filter(column => this._selectedColumnsIds.includes(column.id));
  }

  public onPageChange(event: PageEvent) {
    this.list.setPageSize(event.pageSize);
    this.isAllRowsInPageChecked = !this.isManuallySelectingRows;
    this.list.setPage(event.pageIndex + 1); // this one already reload
    return this.list.load().subscribe();
  }

  // Sorting
  private setupSortEventListener() {
    this.sort.sortChange.pipe(
      switchMap(event => {
        let sort: SearchSort = {sort_on: undefined, sort_order: undefined};
        if (event.direction !== '') {
          sort = {
            sort_on: event.active,
            sort_order: event.direction
          };
        }
        this.list.setSearchSort(sort);
        this.list.resetFilter();
        return this.list.load();
      }),
      takeUntil(this.$destroyed)
    ).subscribe();
  }

  // reodering
  tableDrop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this._selectedColumnsIds, event.previousIndex, event.currentIndex);
    this.list.setSearchSelect(this._selectedColumnsIds);
    this.list.saveSearchModel();
  }

  // Row selection
  onSelectAllRowMatchingSearch() {
    this.isManuallySelectingRows = false;
    this.selectedRowIds = [];

    this.refreshSelectedRowsCount();
    this.checkAllRowsInPageChecked();
    this.emitSelected();
  }

  onSelectAllRowsInPage() {
    this.isAllRowsInPageChecked = !this.isAllRowsInPageChecked;
    if (this.isAllRowsInPageChecked) {
      this.selectedRowIds = Array.from(new Set([
        ...this.selectedRowIds,
        ...this.$tableRows().map(row => row.id)
      ]));
    } else {
      this.selectedRowIds = this.selectedRowIds.filter(id => !this.$tableRows().map(row => row.id).includes(id));
    }

    this.refreshSelectedRowsCount();
    this.checkAllRowsInPageChecked();
    this.emitSelected();
  }

  onRowSelected(itemId: string) {
    // if it was not manually selecting rows, we assume the rows visible were selected by search
    // and we need to switch to manual mode
    // and select all rows within the current page
    // then we remove the row from the list of selected rows
    if (!this.isManuallySelectingRows) {
      this.isManuallySelectingRows = true;
      this.selectedRowIds = this.$tableRows().map(row => row.id);
    }

    // if the row is already selected, remove it from the list
    const index = this.selectedRowIds.indexOf(itemId);
    if (index >= 0) {
      this.selectedRowIds.splice(index, 1);
    } else {
      this.selectedRowIds.push(itemId);
    }
    this.checkAllRowsInPageChecked();
    this.refreshSelectedRowsCount();
    this.emitSelected();
  }

  emitSelected() {
    const selectedItems: SelectedItems = {
      total: this.selectedRowsCount
    };

    if (this.isManuallySelectingRows) {
      selectedItems.ids = this.selectedRowIds;
    } else {
      selectedItems.searchCriteria = this.list.mergedSearchModel;
    }
    this.selectedItems.emit(selectedItems);
  }

  loadSelected() {
    const selectedItems = this.initialSelectedItems;
    if (!selectedItems) {
      return;
    }

    if (selectedItems.ids) {
      this.isManuallySelectingRows = true;
      this.selectedRowIds = selectedItems.ids;
    } else if (selectedItems.searchCriteria) {
      this.isManuallySelectingRows = false;
      this.list.setSearchFilter(selectedItems.searchCriteria);
    }
    this.refreshSelectedRowsCount();
    this.checkAllRowsInPageChecked();
    this.emitSelected();
  }

  onResetSelection() {
    this.isManuallySelectingRows = true;
    this.selectedRowIds = [];
    this.isAllRowsInPageChecked = false;
    this.refreshSelectedRowsCount();
    this.emitSelected();
  }

  isRowSelected(itemId: string): boolean {
    if (!this.isManuallySelectingRows) {
      return true;
    }

    return this.selectedRowIds.includes(itemId);
  }

  refreshSelectedRowsCount() {
    var count = this.selectedRowIds.length;
    if (!this.isManuallySelectingRows) {
      count = this.$paginationMeta().total;
    }

    this.selectedRowsCount = count;
  }

  checkAllRowsInPageChecked() {
    if (!this.isManuallySelectingRows) {
      this.isAllRowsInPageChecked = true;
      return;
    }

    const notSelected = this.$tableRows().filter(row => {
      return !this.selectedRowIds.includes(row.id)
    })

    this.isAllRowsInPageChecked = this.$tableRows().length > 0 && notSelected.length === 0;
  }

  // Sub components Visibility
  onComponentVisibilityChange(componentName: string, action: 'show' | 'hide' | 'toggle' = 'toggle') {
    const mapping = {
      'show': true,
      'hide': false,
    };
    switch (componentName) {
      case 'filtersSelector':
        this.showFiltersSelector = action == 'toggle' ? !this.showFiltersSelector : mapping[action];
        this.showColumnsSelector = false;
        this.showTableExports = false;
        if (this.showFiltersSelector) {
          this.searchFilter = this.list.getSearchModel().filter;
          this.filterService.setInnerFilter(this.searchFilter);
        }
        break;
      case 'columnsSelector':
        this.showColumnsSelector = action == 'toggle' ? !this.showColumnsSelector : mapping[action];
        this.showFiltersSelector = false;
        this.showTableExports = false;
        break;
      case 'tableExports':
        this.showTableExports = action == 'toggle' ? !this.showTableExports : mapping[action];
        this.showFiltersSelector = false;
        this.showColumnsSelector = false;
        break;
      default:
        console.log('unknown component name');
    }
  }

  public openModal(col: any, row: EasTableRow) {
    if (this.columnModals === undefined) {
      console.log('No extra handlers');
      return;
    }
    const component = this.columnModals.find(itm => itm.modalType === col.modalType);
    if (component === undefined) {
      console.log('Extra component not found');
      return
    }

    const cellData = row.row[col.id] ?? col;


    if (cellData.url) {
      this.api.get(cellData.url).subscribe({
        next: data => {
          const dialogRef = this.matDialog.open(component.component, {
            data: data,
            disableClose: true,
            closeOnNavigation: true,
            ...(this.dialogConfig.config(DIALOG_SIZES.LARGE)),
          });
          console.log(dialogRef);
        },
        error: err => {
          this.notify.error(err);
        }
      });
    } else {
      const dialogRef = this.matDialog.open(component.component, {
        disableClose: true,
        closeOnNavigation: true,
        ...(this.dialogConfig.config(DIALOG_SIZES.SMALL)),
        data: {
          row: row,
          context: this.options.context
        }
      });
      console.log(dialogRef);

    }
  }

  public onClick(col: any, cellData: any) {
    if (col.onClick !== undefined) {
      col.onClick(cellData).subscribe();
    }
  }

  public onCollapse(row: EasTableRow) {
    if (!row.expand) {
      return;
    }
    if (row.expand.expanded === EXPAND_STATE.EXPANDED) {
      this.$tableRows.update(rows => {
        return rows.filter(item => {
          if (item.id === row.id) {
            return true;
          }
          // check if the item is a child of the row by comparing the beginning of the path
          const relevantPath = item.expand?.path.slice(0, row.expand?.path.length || 0)
          return isEqual(relevantPath, row.expand?.path || []) ? false : true;
        });
      });
      row.expand.expanded = EXPAND_STATE.COLLAPSED;
    }
  }

  public onExpand(row: EasTableRow) {
    if (!row.expand) {
      return;
    }

    if (!row.expand.expanded || row.expand.expanded === EXPAND_STATE.COLLAPSED) {
      row.expand.expanded = EXPAND_STATE.EXPANDING;
      this.list.loadExpand(row.expand.path).subscribe({
        next: data => {
          this.$tableRows.update(rows => {
            const idx = rows.findIndex(item => item.id === row.id);
            return ([] as EasTableRow[]).concat(rows.slice(0, idx + 1), data, rows.slice(idx + 1));
          });
          if (row.expand) {
            row.expand.expanded = EXPAND_STATE.EXPANDED;
          }
        },
        error: err => {
          console.log(err);
          this.notify.error(err);
          if (row.expand) {
            row.expand.expanded = EXPAND_STATE.COLLAPSED;
          }
        }
      })
    }
  }

  ngOnDestroy() {
    this.$destroyed.next();
    this.$destroyed.complete();
  };
}
